import {Dispatch, SetStateAction, useContext, useEffect, useRef, useState} from 'react';
import {
    DeliveryIntervals,
    ExtraCharge,
    FiltersModel,
    ProposalModel,
    SearchInputModel,
    SearchModel,
    SearchRequest,
    SearchState,
    SortOption,
    SortSettings,
    TechDataModel
} from '../interfaces';
import {
    getDefaultFilters,
    getDefaultSearchModel,
    getDefaultSortOptions,
    getDefaultSortSettings,
    getFilteredAndSorted,
    isEmpty,
    processSearchApiResults
} from '../services';
import {CApi as adminGwApi} from '@services/api';
import {AppContext} from '@app/context/context';
import {useSearchHistory} from './useSearchHistory';
import {useSearchParams} from 'react-router-dom';

interface SearchParams {
    searchText: SearchInputModel;
    setSearchText: Dispatch<SetStateAction<SearchInputModel>>;
    searchBrand: string;
    useAnalogs: boolean;
}

export function useSearchResults({
    searchBrand,
    searchText,
    useAnalogs,
    setSearchText
}: SearchParams): [
    SearchModel,
    SearchModel,
    SortOption[],
    SearchRequest[],
    SearchRequest[],
    (sortOptions: SortOption[]) => void,
    TechDataModel,
    FiltersModel,
    (filters: FiltersModel) => void,
    string,
    SearchState
] {
    const [searchParams] = useSearchParams();
    const {state} = useContext(AppContext);
    const clientSelected = state.selectedClient?.client;

    const searchRequestRef = useRef<SearchRequest>({
        brand: '',
        article: '',
        articleName: '',
        client: ''
    });
    const apiResults = useRef<[]>([]);
    const skippedApiResults = useRef<[]>([]);
    const sourceResults = useRef<SearchModel>(getDefaultSearchModel());
    const sourceSkippedResults = useRef<SearchModel>(getDefaultSearchModel());
    const sortOptionsRef = useRef<SortOption[]>(getDefaultSortOptions());
    const sortSettings = useRef<SortSettings>(getDefaultSortSettings());
    const filtersRef = useRef<FiltersModel>(getDefaultFilters());

    const [results, setResults] = useState<SearchModel>(sourceResults.current);
    const [skippedResults, setSkippedResults] = useState<SearchModel>(sourceSkippedResults.current);
    const [sortOptions, setSortOptions] = useState<SortOption[]>(sortOptionsRef.current);
    const [techData, setTechData] = useState<TechDataModel>({
        client: undefined,
        normalizedSubject: undefined,
        trimBest: undefined,
        analogs: []
    });
    const [filters, setFilters] = useState<FiltersModel>(filtersRef.current);
    const [searchState, setSearchState] = useState<SearchState>(SearchState.None);
    const [requestTime, setRequestTime] = useState('0,00'); // продолжительность проценки

    const [searchHistory, setSearchHistory] = useSearchHistory();

    const urlClient = decodeURIComponent(searchParams.get('client') || '');
    const urlNonce = decodeURIComponent(searchParams.get('nonce') || '');

    useEffect(() => {
        if (!searchBrand || !clientSelected) return;

        const article = searchText.cleanedValue;
        if (!article) return;

        let ignore = false;

        // Время начала проценки
        const startTime = performance.now();

        searchRequestRef.current = {
            article: searchText.cleanedValue,
            brand: searchBrand,
            client: clientSelected
        } as SearchRequest;

        // clear everything
        setSearchState(SearchState.Searching);
        apiResults.current = [];
        sourceResults.current = getDefaultSearchModel();
        setResults(sourceResults.current);

        skippedApiResults.current = [];
        sourceSkippedResults.current = getDefaultSearchModel();
        setSkippedResults(sourceSkippedResults.current);

        sortOptionsRef.current = getDefaultSortOptions();
        setSortOptions(sortOptionsRef.current);

        filtersRef.current = getDefaultFilters();
        setFilters(filtersRef.current);

        const resultsPromise = adminGwApi().suggest.suggestCreate({
            ...searchRequestRef.current,
            useAnalogs: useAnalogs
        });

        resultsPromise
            .then((r) => {
                if (!ignore) {
                    console.log('r', r);

                    //сохраняем extraCharges и deliveryIntervals для модальных окон
                    const extraChargesPool: ExtraCharge[] = r.data.extraCharges ?? [];
                    const deliveryIntervalsPool: DeliveryIntervals[] =
                        r.data.deliveryIntervals ?? [];

                    /*------------ Обработка proposals ------------*/
                    const rawItems = r.data.proposals ?? [];
                    apiResults.current = rawItems;

                    if (rawItems.length === 0) {
                        setSearchState(SearchState.Empty);
                    } else {
                        searchRequestRef.current.articleName = rawItems[0].articleName;
                    }

                    // подмешиваем нужный extraCharge и deliveryInterval в каждый proposal
                    rawItems.forEach((p: ProposalModel) => {
                        const sys = p.systemInfo ?? {};

                        const common =
                            typeof sys.commonExtraChargeIdx === 'number'
                                ? extraChargesPool[sys.commonExtraChargeIdx]
                                : undefined;

                        const additional = Array.isArray(sys.additionalExtraChargesIdxs)
                            ? sys.additionalExtraChargesIdxs
                                  .map((i) => extraChargesPool[i])
                                  .filter(Boolean)
                            : [];

                        const deliveryInterval =
                            typeof sys.deliveryIdx === 'number'
                                ? deliveryIntervalsPool[sys.deliveryIdx]
                                : undefined;

                        p.extraCharges = {common, additional};
                        p.deliveryInterval = deliveryInterval;
                    });

                    const [results, filters] = processSearchApiResults({
                        searchResults: [rawItems],
                        searchRequest: searchRequestRef.current
                    });

                    sourceResults.current = results;
                    filtersRef.current = filters;
                    setFilters(filtersRef.current);
                    const filteredAndSorted = getFilteredAndSorted(
                        results,
                        filtersRef.current,
                        sortOptionsRef.current,
                        sortSettings.current
                    );
                    setResults(filteredAndSorted);
                    /*------------ Обработка proposals ------------*/

                    /*------------ Обработка skippedProposals ------------*/
                    const rawSkippedItems = r.data.skippedProposals ?? [];
                    skippedApiResults.current = rawSkippedItems;

                    // подмешиваем нужный extraCharge и deliveryInterval в каждый skippedProposal
                    rawSkippedItems.forEach((p: ProposalModel) => {
                        const sys = p.systemInfo ?? {};

                        const common =
                            typeof sys.commonExtraChargeIdx === 'number'
                                ? extraChargesPool[sys.commonExtraChargeIdx]
                                : undefined;

                        const additional = Array.isArray(sys.additionalExtraChargesIdxs)
                            ? sys.additionalExtraChargesIdxs
                                  .map((i) => extraChargesPool[i])
                                  .filter(Boolean)
                            : [];

                        const deliveryInterval =
                            typeof sys.deliveryIdx === 'number'
                                ? deliveryIntervalsPool[sys.deliveryIdx]
                                : undefined;

                        p.extraCharges = {common, additional};
                        p.deliveryInterval = deliveryInterval;

                        // если requestedArticle пустой, берём из searchRequest
                        if (!p.requestedArticle) {
                            p.requestedArticle = searchRequestRef.current.article;
                        }
                    });

                    const [processedSkippedResults] = processSearchApiResults({
                        searchResults: [rawSkippedItems],
                        searchRequest: searchRequestRef.current
                    });

                    sourceSkippedResults.current = processedSkippedResults;

                    const filteredAndSortedSkipped = getFilteredAndSorted(
                        processedSkippedResults,
                        filtersRef.current,
                        sortOptionsRef.current,
                        sortSettings.current
                    );

                    setSkippedResults(filteredAndSortedSkipped);
                    /*------------ Обработка skippedProposals ------------*/

                    // Для вкладки Технической информации
                    const {
                        client = {},
                        normalizedSubject = {},
                        trimBest = {},
                        analogs = [],
                        stats = {}
                    } = r.data;
                    setTechData({
                        client,
                        normalizedSubject,
                        trimBest,
                        analogs,
                        stats
                    });

                    const isEmptyAfterFetch = isEmpty(results);
                    if (!isEmptyAfterFetch) {
                        setSearchState(SearchState.Found);
                        setSearchText((prev) => {
                            return {
                                cleanedValue: prev.cleanedValue,
                                originalValue: prev.cleanedValue,
                                needToSuggestBrands: false
                            };
                        });
                    }
                }
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                if (!ignore) {
                    // Время окончания проценки + вычисляем разницу
                    const endTime = performance.now();
                    const durationSeconds = ((endTime - startTime) / 1000)
                        .toFixed(2)
                        .replace('.', ',');
                    setRequestTime(durationSeconds);
                }

                addToSearchHistory(searchRequestRef.current);
            });

        return () => {
            ignore = true;
        };
    }, [searchBrand, searchText.cleanedValue, useAnalogs, urlClient, urlNonce]);

    function handleFiltersChanged(filters: FiltersModel) {
        const filteredAndSortedResults = getFilteredAndSorted(
            sourceResults.current,
            filters,
            sortOptionsRef.current,
            sortSettings.current
        );

        const filteredAndSortedSkipped = getFilteredAndSorted(
            sourceSkippedResults.current,
            filters,
            sortOptionsRef.current,
            sortSettings.current
        );

        const isEmptyAfterFilters = isEmpty(filteredAndSortedResults);

        filtersRef.current = filters;
        setFilters(filtersRef.current);
        setResults(filteredAndSortedResults);
        setSkippedResults(filteredAndSortedSkipped);
    }

    function handleSortOptionChanged(sortOptions: SortOption[]) {
        sortOptionsRef.current = sortOptions;
        sortSettings.current = {...sortSettings.current, sortArticles: true};
        setSortOptions(sortOptionsRef.current);

        const filteredAndSortedResults = getFilteredAndSorted(
            sourceResults.current,
            filters,
            sortOptionsRef.current,
            sortSettings.current
        );

        const filteredAndSortedSkipped = getFilteredAndSorted(
            sourceSkippedResults.current,
            filters,
            sortOptionsRef.current,
            sortSettings.current
        );

        setResults(filteredAndSortedResults);
        setSkippedResults(filteredAndSortedSkipped);
    }

    function addToSearchHistory(searchRequest: SearchRequest) {
        setSearchHistory((prev) => {
            const excludeCurrent = () =>
                prev.filter(
                    (x) => x.article !== searchRequest.article || x.brand !== searchRequest.brand
                );

            return [searchRequest, ...excludeCurrent().slice(0, 9)];
        });
    }

    return [
        results,
        skippedResults,
        sortOptions,
        apiResults.current,
        skippedApiResults.current,
        handleSortOptionChanged,
        techData,
        filters,
        handleFiltersChanged,
        requestTime,
        searchState
    ];
}
